import { format, parseISO } from 'date-fns';
import { SimpleConfigBaseFilter } from 'gung-list';
import { PunchoutAuditListItem } from '../../models/punchout';

const UNKNOWN = 'UNKNOWN';

/**
 * Filters for the punchout audit lists. These are plain classes, not injectable services: none
 * of them need dependencies, and each audit list config instantiates the set it wants.
 */

/** Groups by the resolved config label, so options read as config names rather than mongo ids. */
export class PunchoutAuditConfigFilter extends SimpleConfigBaseFilter<PunchoutAuditListItem> {
  // Config names are data, not translation keys.
  override translateValues = false;

  getName(): string {
    return 'PUNCHOUT_AUDIT_CONFIG';
  }

  getOptionIds(item: PunchoutAuditListItem): string[] {
    return [item.configLabel || UNKNOWN];
  }

  getOptionName(key: string): string {
    return key;
  }
}

/** Groups by calendar month of the entry timestamp, newest month first. */
export class PunchoutAuditMonthFilter extends SimpleConfigBaseFilter<PunchoutAuditListItem> {
  override translateValues = false;

  getName(): string {
    return 'PUNCHOUT_AUDIT_MONTH';
  }

  getOptionIds(item: PunchoutAuditListItem): string[] {
    return [monthOf(item.timestamp)];
  }

  getOptionName(key: string): string {
    return key;
  }

  override sort(a: { id?: string } | string, b: { id?: string } | string): number {
    return keyOf(b).localeCompare(keyOf(a));
  }
}

/** Cart transfers only: whether a converted payload was stored at all. */
export class PunchoutAuditOutcomeFilter extends SimpleConfigBaseFilter<PunchoutAuditListItem> {
  getName(): string {
    return 'PUNCHOUT_AUDIT_OUTCOME';
  }

  getOptionIds(item: PunchoutAuditListItem): string[] {
    return [item.outcome ?? UNKNOWN];
  }

  getOptionName(key: string): string {
    return key;
  }
}

/** Order requests only: the intake lifecycle status. */
export class PunchoutAuditStatusFilter extends SimpleConfigBaseFilter<PunchoutAuditListItem> {
  getName(): string {
    return 'PUNCHOUT_AUDIT_STATUS';
  }

  getOptionIds(item: PunchoutAuditListItem): string[] {
    return [item.status ?? UNKNOWN];
  }

  getOptionName(key: string): string {
    return key;
  }
}

function monthOf(timestamp?: string): string {
  if (!timestamp) {
    return UNKNOWN;
  }
  try {
    return format(parseISO(timestamp), 'yyyy-MM');
  } catch {
    return UNKNOWN;
  }
}

function keyOf(value: { id?: string } | string): string {
  return typeof value === 'string' ? value : (value?.id ?? '');
}
